/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { FeatureCollection } from 'geojson';
import type { View } from 'ol';
import {
  createEmpty,
  extend as extendExtent,
  isEmpty as isEmptyExtent,
} from 'ol/extent';
import GeoJSON from 'ol/format/GeoJSON';
import React, { useRef } from 'react';
import { defaultBbox as defaultBboxCore } from '@opengeoweb/core';
import type { MapPreset } from '@opengeoweb/store';
import { LayerType, generateMapId } from '@opengeoweb/webmap';
import {
  OpenLayersFeatureLayer,
  OpenLayersLayer,
  OpenLayersMapView,
  defaultLayers,
  genericOpenLayersFeatureStyle,
  OpenLayersZoomControl,
  MapControls,
  viewUtils,
} from '@opengeoweb/webmap-react';
import type { OpenLayersLayerProps } from '@opengeoweb/webmap-react';

export const EXTENT_PADDING = 50;
function getGeojsonExtent(
  featureCollection: FeatureCollection,
  viewSrs: string,
  dataSrs = 'EPSG:4326', // change if GeoJSON is not lon/lat
): number[] {
  const format = new GeoJSON();
  const features = format.readFeatures(featureCollection, {
    dataProjection: dataSrs,
    featureProjection: viewSrs,
  });

  const extent = createEmpty();
  for (const feat of features) {
    const geo = feat.getGeometry();
    if (geo) {
      extendExtent(extent, geo.getExtent());
    }
  }
  return extent;
}

interface WarningsMapViewProps {
  geojsonFeature?: FeatureCollection | undefined;
  defaultMapPreset?: MapPreset;
  isSnapshot?: boolean;
}
export const WarningsMapView: React.FC<WarningsMapViewProps> = ({
  geojsonFeature,
  defaultMapPreset,
  isSnapshot = false,
}: WarningsMapViewProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const defaultMapPresetBbox = geojsonFeature?.features.length
    ? undefined
    : defaultBboxCore;
  const defaultBbox = defaultMapPreset?.proj || defaultMapPresetBbox;

  const defaultBaseLayer =
    defaultMapPreset?.layers?.find(
      (layer) => layer.layerType === LayerType.baseLayer,
    ) || defaultLayers.baseLayerGrey;
  const defaultOverLayer =
    defaultMapPreset?.layers?.find(
      (layer) => layer.layerType === LayerType.overLayer,
    ) || defaultLayers.overLayer;

  const mapId = React.useRef<string>(
    `WarningsMapView_${generateMapId()}`,
  ).current;

  const warningLayer: OpenLayersLayerProps = {
    id: `${mapId}geojsonfeature`,
    geojson: geojsonFeature,
    layerType: LayerType.featureLayer,
  };

  const viewRef = useRef<View>(viewUtils.makeView());

  React.useEffect(() => {
    if (viewRef.current.getProjection().getCode() !== defaultBbox?.srs) {
      viewRef.current = viewUtils.makeView(defaultBbox?.srs);
    }

    const isNew = defaultBbox?.bbox && !geojsonFeature?.features.length;

    if (isNew) {
      viewUtils.setViewFromExtent(viewRef.current, defaultBbox?.bbox);
      return;
    }

    if (geojsonFeature?.features?.length) {
      const viewSrs = viewRef.current.getProjection().getCode();
      const extent = getGeojsonExtent(geojsonFeature, viewSrs, 'EPSG:4326');

      if (!isEmptyExtent(extent)) {
        const el = containerRef.current;
        const rect = el?.getBoundingClientRect();
        const size =
          rect && rect.width > 0 && rect.height > 0
            ? ([Math.round(rect.width), Math.round(rect.height)] as [
                number,
                number,
              ])
            : undefined;

        viewRef.current.fit(extent, {
          size,
          padding: [
            EXTENT_PADDING,
            EXTENT_PADDING,
            EXTENT_PADDING,
            EXTENT_PADDING,
          ],
          maxZoom: 12,
          duration: 0,
        });

        return;
      }
    }

    // fallback
    viewUtils.setViewFromFeature(viewRef.current, geojsonFeature);
  }, [defaultBbox, geojsonFeature]);

  return (
    <div ref={containerRef} style={{ height: '200px', width: '100%' }}>
      <OpenLayersMapView
        holdShiftToScroll={true}
        mapId={mapId}
        view={viewRef.current}
      >
        <MapControls style={{ top: 0 }}>
          <OpenLayersZoomControl
            homeExtent={viewRef.current?.calculateExtent()}
          />
        </MapControls>
        {!isSnapshot && (
          <OpenLayersLayer {...defaultBaseLayer} id={`${mapId}_baseLayer`} />
        )}
        {!!warningLayer.geojson && (
          <OpenLayersFeatureLayer
            featureCollection={warningLayer.geojson}
            style={genericOpenLayersFeatureStyle}
            zIndex={1000}
          />
        )}
        {!isSnapshot && (
          <OpenLayersLayer {...defaultOverLayer} id={`${mapId}_overlayer`} />
        )}
      </OpenLayersMapView>
    </div>
  );
};
